import classNames from 'classnames'
import { useEffect, useState } from 'react'
import LazyLoad from 'react-lazyload'

import bannedImg from '@/assets/banned.png'
import unofficialImg from '@/assets/unofficial.png'
import toastService from '@/services/toast.service'
import { useAppSelector } from '@/store/hooks'
import { GenericCardType } from '@/types/card'
import { CardType, PageCardType } from '@/types/card'
import { bracketsArticleUrl } from '@/utils/constants'
import { useCard } from '@/utils/fetcher'
import { formatUrl } from '@/utils/helpers'
import usePatreonTier from '@/utils/usePatreonTier'

import Icon from '../Icon/Icon'
import OutboundLink from '../OutboundLink/OutboundLink'

import styles from './Card.module.scss'
import CardButtons from './CardButtons/CardButtons'
import CardImage from './CardImage'
import CardLabel from './CardLabel/CardLabel'
import CardLoading from './CardLoading/CardLoading'
import CardPrices from './CardPrices/CardPrices'
import CardSalt from './CardSalt/CardSalt'

export const GAME_CHANGERS = [
  'Ad Nauseam',
  'Ancient Tomb',
  'Aura Shards',
  "Bolas's Citadel",
  'Braids, Cabal Minion',
  'Chrome Mox',
  'Coalition Victory',
  'Consecrated Sphinx',
  'Crop Rotation',
  'Cyclonic Rift',
  'Deflecting Swat',
  'Demonic Tutor',
  'Drannith Magistrate',
  'Enlightened Tutor',
  'Expropriate',
  'Field of the Dead',
  'Fierce Guardianship',
  'Food Chain',
  'Force of Will',
  "Gaea's Cradle",
  'Gamble',
  'Gifts Ungiven',
  'Glacial Chasm',
  'Grand Arbiter Augustin IV',
  'Grim Monolith',
  'Humility',
  'Imperial Seal',
  'Intuition',
  "Jeska's Will",
  'Jin-Gitaxias, Core Augur',
  'Kinnan, Bonder Prodigy',
  "Lion's Eye Diamond",
  'Mana Vault',
  "Mishra's Workshop",
  'Mox Diamond',
  'Mystical Tutor',
  'Narset, Parter of Veils',
  'Natural Order',
  'Necropotence',
  'Notion Thief',
  'Opposition Agent',
  'Orcish Bowmasters',
  'Panoptic Mirror',
  'Rhystic Study',
  'Seedborn Muse',
  "Serra's Sanctum",
  'Smothering Tithe',
  'Survival of the Fittest',
  'Sway of the Stars',
  "Teferi's Protection",
  'Tergrid, God of Fright',
  "Thassa's Oracle",
  'The One Ring',
  'The Tabernacle at Pendrell Vale',
  'Underworld Breach',
  'Urza, Lord High Artificer',
  'Vampiric Tutor',
  'Vorinclex, Voice of Hunger',
  'Winota, Joiner of Forces',
  'Worldly Tutor',
  "Yuriko, the Tiger's Shadow",
]

type Props = {
  background?: boolean
  card?: GenericCardType
  className?: string
  fullCard?: CardType
  isPreview?: boolean
  label?: React.ReactNode
  name?: string
  new?: boolean
  onlyBack?: boolean
  onlyFront?: boolean
  pageCommander?: PageCardType
  reverse?: boolean
  scalable?: boolean
  sideBySide?: boolean
  target?: string
}

const CardLazy = (props: Props) => {
  const { card, fullCard, isPreview, name, onlyBack, onlyFront, pageCommander, reverse, scalable, sideBySide, target } =
    props
  const alternateArt = useAppSelector((state) => state.user.alternateArt)
  const patreonTier = usePatreonTier()
  const [imageIsLoaded, setImageIsLoaded] = useState(false)
  const [isRotated, setIsRotated] = useState(false)
  const [isContentWarningHidden, setIsContentWarningHidden] = useState(false)

  useEffect(() => {
    setIsRotated(false)
    setIsContentWarningHidden(false)
  }, [card])

  // Fetch data
  const { data: fetchedData, error } = useCard({ card: card as GenericCardType, name })
  const data =
    fetchedData ||
    (fullCard ? { ...fullCard, url: fullCard.sanitized || formatUrl(fullCard.name || name || '') } : undefined)

  if (error) return isPreview ? <>Failed to load {<span className={styles.error}>{name}</span>}</> : null

  const { banned, content_warning: hasContentWarning, new: isNew, prices, unofficial } = data || {}
  const isGameChanger = GAME_CHANGERS.includes(data?.name || '')

  // image_uris
  let image_uris: string[] | undefined = undefined
  if (patreonTier === 'rare' && alternateArt && !name?.includes('|')) {
    if (card && card.cards && card.cards.length > 1 && card.is_partner) {
      image_uris = [0, 1].map((i) =>
        card.cards
          ? alternateArt[card.cards[i]?.url || '']
            ? alternateArt[card.cards[i]?.url || ''][0]
            : data
              ? data.image_uris[i]
              : ''
          : '',
      )
    } else if (data) {
      image_uris = alternateArt[data.url || data.sanitized || ''] || data.image_uris
    }
  } else if (data) {
    image_uris = data.image_uris
  }

  // If no url given, build a url from the name
  // If given url is null, pass in undefined (no link, Card is unclickable)
  // Otherwise, use the given url
  const url =
    card?.url !== undefined
      ? card.url === null
        ? undefined
        : card.url
      : `/${data?.legal_commander ? 'commanders' : 'cards'}/${formatUrl((name || data?.name)?.split('|')[0] || '')}`

  return (
    <>
      {banned ? (
        <div className={styles.banned}>
          <img alt='Banned' src={bannedImg.src} />
        </div>
      ) : unofficial ? (
        <div className={styles.banned}>
          <img alt='Unofficial' src={unofficialImg.src} />
        </div>
      ) : null}
      {data && (
        <>
          {hasContentWarning && !isContentWarningHidden && (
            <div className={styles.hasWarning}>
              <p className={styles.warningIcon}>
                <Icon icon='circleExclamation' />
              </p>
              <p className={styles.warningNote}>
                <strong>Please note:</strong> This card is not in compliance with Magic’s community standards as a
                result of racist artwork or text, the use of demographic stereotypes, and/or parallels to sensitive
                historical events.
              </p>
              <p>
                <button className='btn btn-outline-light btn-sm' onClick={() => setIsContentWarningHidden(true)}>
                  Show Card Image
                </button>
              </p>
              <p>
                <OutboundLink
                  className='btn btn-outline-light btn-sm'
                  url='https://magic.wizards.com/en/news/announcements/depictions-racism-magic-2020-06-10'
                >
                  More Info
                </OutboundLink>
              </p>
            </div>
          )}
          {!hasContentWarning && (
            <CardButtons
              card={data}
              isRotated={isRotated}
              pageCommander={pageCommander}
              scalable={scalable}
              setIsRotated={setIsRotated}
            />
          )}
          {image_uris && (
            <CardImage
              image_uris={image_uris}
              isRotated={isRotated}
              name={data.name}
              names={data.names}
              onLoad={() => setImageIsLoaded(true)}
              onlyBack={onlyBack}
              onlyFront={onlyFront}
              reverse={reverse}
              scalable={scalable}
              sideBySide={sideBySide}
              target={target}
              url={url}
            >
              {isGameChanger ? (
                <OutboundLink url={bracketsArticleUrl}>
                  <div className={styles.gameChanger}>
                    <span>GC</span>
                  </div>
                </OutboundLink>
              ) : props.new || isNew ? (
                <div className={styles.new}>
                  <span>NEW</span>
                </div>
              ) : undefined}
              <CardSalt salt={data.salt} />
            </CardImage>
          )}
        </>
      )}
      {!imageIsLoaded && <CardLoading />}
      {prices && !hasContentWarning && <CardPrices card={data as CardType} scalable={scalable} />}
    </>
  )
}

const Card = (props: Props) => {
  const namesUnderCards = useAppSelector((state) => state.user.namesUnderCards)

  const name = props.card?.name || props.name || props.fullCard?.name || ''

  return (
    <div
      className={classNames(styles.container, props.className, {
        [styles.sideBySide]: props.sideBySide,
        [styles.scalable]: props.scalable,
      })}
    >
      {!namesUnderCards && (
        <div
          className={classNames(styles.nameWrapper, {
            [styles.scalableNameWrapper]: props.scalable,
          })}
          onClick={() => {
            toastService.createToast(`Copied ${name}`)
            navigator.clipboard.writeText(name)
          }}
        >
          <span className={styles.name}>{name}</span>
        </div>
      )}
      <LazyLoad className={classNames({ [styles.sideBySide]: props.sideBySide })} height={359.19} once>
        <CardLazy {...props} pageCommander={props.pageCommander} />
      </LazyLoad>
      {namesUnderCards && <div className={styles.nameUnderCard}>{name}</div>}
      <CardLabel
        label={props.label || props.card?.label}
        num_decks={props.card?.num_decks}
        pageCommander={props.pageCommander}
        potential_decks={props.card?.potential_decks}
        synergy={props.card?.synergy}
      />
    </div>
  )
}

export default Card
